import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Power, PowerOff, Code, Target, FolderOpen, Database, MessageSquare, Settings as SettingsIcon, Globe, ChevronDown, ChevronRight, X, Check, AlertCircle, Key, CheckCircle, XCircle, Zap } from 'lucide-react';
import { fetchAllMCPServers, createMCPServer, updateMCPServer, deleteMCPServer, MCP_TEMPLATES } from '../services/mcpApi';
import { fetchTeams } from '../services/teamsApi';
import { fetchUsers } from '../services/usersApi';
import { API_BASE_URL } from '../config/api';

const ICON_MAP = {
  Target,
  Code,
  FolderOpen,
  Database,
  MessageSquare,
  Settings: SettingsIcon,
  Globe
};

const MCPSettings = () => {
  const [mcpServers, setMcpServers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingServer, setEditingServer] = useState(null);
  const [expandedServer, setExpandedServer] = useState(null);
  const [error, setError] = useState(null);
  const [testingServer, setTestingServer] = useState(null);
  const [testResults, setTestResults] = useState({});

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [serversData, teamsData, usersData] = await Promise.all([
        fetchAllMCPServers(),
        fetchTeams(),
        fetchUsers()
      ]);
      setMcpServers(serversData);
      setTeams(teamsData);
      setUsers(usersData);
    } catch (err) {
      console.error('Failed to load MCP data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingServer(null);
    setShowCreateModal(true);
  };

  const handleEdit = (server) => {
    setEditingServer(server);
    setShowCreateModal(true);
  };

  const handleDelete = async (serverId) => {
    if (!window.confirm('Are you sure you want to delete this MCP server?')) {
      return;
    }

    try {
      await deleteMCPServer(serverId);
      setMcpServers(prev => prev.filter(s => s.id !== serverId));
    } catch (err) {
      alert('Failed to delete MCP server: ' + err.message);
    }
  };

  const handleToggleActive = async (server) => {
    try {
      const updated = await updateMCPServer(server.id, {
        ...server,
        isActive: !server.isActive
      });
      setMcpServers(prev => prev.map(s => s.id === updated.id ? updated : s));
    } catch (err) {
      alert('Failed to update MCP server: ' + err.message);
    }
  };

  const handleTestConnection = async (server) => {
    setTestingServer(server.id);
    setTestResults(prev => ({ ...prev, [server.id]: { status: 'testing' } }));

    try {
      const response = await fetch(`${API_BASE_URL}/mcp/${server.id}/test`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();

      if (response.ok) {
        setTestResults(prev => ({
          ...prev,
          [server.id]: {
            status: 'success',
            message: result.message || 'Connection successful!',
            tools: result.tools || [],
            timestamp: new Date().toISOString()
          }
        }));
      } else {
        setTestResults(prev => ({
          ...prev,
          [server.id]: {
            status: 'error',
            message: result.error || 'Connection failed',
            details: result.details,
            timestamp: new Date().toISOString()
          }
        }));
      }
    } catch (err) {
      setTestResults(prev => ({
        ...prev,
        [server.id]: {
          status: 'error',
          message: 'Failed to test connection',
          details: err.message,
          timestamp: new Date().toISOString()
        }
      }));
    } finally {
      setTestingServer(null);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold">MCP Servers</h3>
          <p className="text-sm text-gray-600 mt-1">
            Configure Model Context Protocol servers for enhanced AI capabilities
          </p>
        </div>
        <button
          onClick={handleCreate}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add MCP Server
        </button>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-800">Error loading MCP servers</p>
            <p className="text-xs text-red-600 mt-1">{error}</p>
          </div>
        </div>
      )}

      {mcpServers.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed">
          <Code className="w-12 h-12 mx-auto mb-3 text-gray-400" />
          <p className="text-gray-600 mb-2">No MCP servers configured</p>
          <p className="text-sm text-gray-500 mb-4">
            Add MCP servers to extend AI capabilities with external tools and data sources
          </p>
          <button
            onClick={handleCreate}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors inline-flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Your First MCP Server
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {mcpServers.map(server => {
            const Icon = ICON_MAP[server.icon] || Code;
            const isExpanded = expandedServer === server.id;

            return (
              <div key={server.id} className="border rounded-lg overflow-hidden">
                <div className="p-4 bg-white hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3 flex-1">
                      <div className={`p-2 rounded-lg ${server.isActive ? 'bg-blue-100' : 'bg-gray-100'}`}>
                        <Icon className={`w-5 h-5 ${server.isActive ? 'text-blue-600' : 'text-gray-400'}`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-medium">{server.name}</h4>
                          <span className={`px-2 py-0.5 text-xs rounded-full ${
                            server.isActive
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-600'
                          }`}>
                            {server.isActive ? 'Active' : 'Inactive'}
                          </span>
                          {server.isGlobal && (
                            <span className="px-2 py-0.5 text-xs rounded-full bg-purple-100 text-purple-800">
                              Global
                            </span>
                          )}
                        </div>
                        {server.description && (
                          <p className="text-sm text-gray-600 mb-2">{server.description}</p>
                        )}
                        <div className="flex items-center gap-4 text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            <Code className="w-3 h-3" />
                            {server.type}
                          </span>
                          {server.teamPermissions?.length > 0 && (
                            <span>{server.teamPermissions.length} team{server.teamPermissions.length !== 1 ? 's' : ''}</span>
                          )}
                          {server.userPermissions?.length > 0 && (
                            <span>{server.userPermissions.length} user{server.userPermissions.length !== 1 ? 's' : ''}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setExpandedServer(isExpanded ? null : server.id)}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        title="View details"
                      >
                        {isExpanded ? (
                          <ChevronDown className="w-4 h-4 text-gray-600" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-gray-600" />
                        )}
                      </button>
                      <button
                        onClick={() => handleToggleActive(server)}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        title={server.isActive ? 'Deactivate' : 'Activate'}
                      >
                        {server.isActive ? (
                          <Power className="w-4 h-4 text-green-600" />
                        ) : (
                          <PowerOff className="w-4 h-4 text-gray-400" />
                        )}
                      </button>
                      <button
                        onClick={() => handleEdit(server)}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        title="Edit"
                      >
                        <Edit2 className="w-4 h-4 text-gray-600" />
                      </button>
                      <button
                        onClick={() => handleDelete(server.id)}
                        className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </button>
                    </div>
                  </div>
                </div>

                {isExpanded && (
                  <div className="border-t bg-gray-50 p-4 space-y-3">
                    {/* Google Ads OAuth Authorization (for both google-ads and keyword-planner) */}
                    {(server.type === 'google-ads' || server.type === 'google-ads-keyword-planner') && (
                      <GoogleAdsOAuthSection server={server} onUpdate={loadData} />
                    )}

                    {/* Google Ads Query Mode Indicator */}
                    {server.type === 'google-ads' && (
                      <div className={`p-3 border rounded-lg ${
                        server.useSegmentedQueries
                          ? 'bg-green-50 border-green-200'
                          : 'bg-yellow-50 border-yellow-200'
                      }`}>
                        <div className="flex items-start gap-2">
                          {server.useSegmentedQueries ? (
                            <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                          ) : (
                            <AlertCircle className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                          )}
                          <div className="flex-1">
                            <h5 className="text-sm font-semibold mb-0.5">
                              {server.useSegmentedQueries ? 'Segmented Query Mode Active' : 'Standard Query Mode'}
                            </h5>
                            <p className="text-xs text-gray-700">
                              {server.useSegmentedQueries
                                ? 'Queries automatically include segments.date for accurate metric aggregation.'
                                : 'Using standard queries - may return cached/phantom data for aggregated metrics.'
                              }
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    <div>
                      <h5 className="text-sm font-medium mb-1">Command</h5>
                      <code className="text-xs bg-white px-2 py-1 rounded border block overflow-x-auto">
                        {server.command} {Array.isArray(server.args) ? server.args.join(' ') : ''}
                      </code>
                    </div>
                    {server.env && Object.keys(server.env).length > 0 && (
                      <div>
                        <h5 className="text-sm font-medium mb-1">Environment Variables</h5>
                        <div className="text-xs bg-white px-2 py-1 rounded border">
                          {Object.keys(server.env).map(key => (
                            <div key={key} className="py-0.5">
                              <span className="font-mono text-blue-600">{key}</span>
                              <span className="text-gray-500">: {server.env[key] ? '•••••' : '(not set)'}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {server.teamPermissions?.length > 0 && (
                      <div>
                        <h5 className="text-sm font-medium mb-1">Teams with Access</h5>
                        <div className="flex flex-wrap gap-2">
                          {server.teamPermissions.map(tp => (
                            <span key={tp.id} className="px-2 py-1 bg-white rounded border text-xs">
                              {tp.team.name}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Test Connection Button */}
                    <div className="pt-2 border-t">
                      <button
                        onClick={() => handleTestConnection(server)}
                        disabled={testingServer === server.id}
                        className={`w-full px-4 py-2 rounded-lg font-medium text-sm transition-colors flex items-center justify-center gap-2 ${
                          testingServer === server.id
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-blue-600 text-white hover:bg-blue-700'
                        }`}
                      >
                        <Zap className="w-4 h-4" />
                        {testingServer === server.id ? 'Testing Connection...' : 'Test Connection'}
                      </button>

                      {/* Test Results */}
                      {testResults[server.id] && (
                        <div className={`mt-3 p-3 rounded-lg border ${
                          testResults[server.id].status === 'success'
                            ? 'bg-green-50 border-green-200'
                            : testResults[server.id].status === 'error'
                            ? 'bg-red-50 border-red-200'
                            : 'bg-blue-50 border-blue-200'
                        }`}>
                          <div className="flex items-start gap-2">
                            {testResults[server.id].status === 'success' && (
                              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                            )}
                            {testResults[server.id].status === 'error' && (
                              <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                            )}
                            {testResults[server.id].status === 'testing' && (
                              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 flex-shrink-0"></div>
                            )}
                            <div className="flex-1">
                              <p className={`text-sm font-medium ${
                                testResults[server.id].status === 'success'
                                  ? 'text-green-800'
                                  : testResults[server.id].status === 'error'
                                  ? 'text-red-800'
                                  : 'text-blue-800'
                              }`}>
                                {testResults[server.id].message}
                              </p>
                              {testResults[server.id].details && (
                                <p className="text-xs text-gray-600 mt-1">
                                  {testResults[server.id].details}
                                </p>
                              )}
                              {testResults[server.id].tools && testResults[server.id].tools.length > 0 && (
                                <div className="mt-2">
                                  <p className="text-xs font-medium text-gray-700 mb-1">
                                    Available Tools ({testResults[server.id].tools.length}):
                                  </p>
                                  <div className="flex flex-wrap gap-1">
                                    {testResults[server.id].tools.slice(0, 5).map((tool, idx) => (
                                      <span key={idx} className="px-2 py-0.5 bg-white rounded border text-xs text-gray-700">
                                        {tool.name}
                                      </span>
                                    ))}
                                    {testResults[server.id].tools.length > 5 && (
                                      <span className="px-2 py-0.5 bg-gray-100 rounded text-xs text-gray-600">
                                        +{testResults[server.id].tools.length - 5} more
                                      </span>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showCreateModal && (
        <MCPServerModal
          server={editingServer}
          teams={teams}
          users={users}
          onClose={() => {
            setShowCreateModal(false);
            setEditingServer(null);
          }}
          onSave={async (serverData) => {
            try {
              if (editingServer) {
                const updated = await updateMCPServer(editingServer.id, serverData);
                setMcpServers(prev => prev.map(s => s.id === updated.id ? updated : s));
              } else {
                const created = await createMCPServer(serverData);
                setMcpServers(prev => [...prev, created]);
              }
              setShowCreateModal(false);
              setEditingServer(null);
            } catch (err) {
              alert('Failed to save MCP server: ' + err.message);
            }
          }}
        />
      )}
    </div>
  );
};

const MCPServerModal = ({ server, teams, users, onClose, onSave }) => {
  const [selectedTemplate, setSelectedTemplate] = useState('custom');
  const [formData, setFormData] = useState({
    name: server?.name || '',
    type: server?.type || '',
    description: server?.description || '',
    command: server?.command || '',
    args: server?.args || [],
    env: server?.env || {},
    isGlobal: server?.isGlobal || false,
    isActive: server?.isActive !== undefined ? server.isActive : true,
    icon: server?.icon || 'Code',
    useSegmentedQueries: server?.useSegmentedQueries !== undefined ? server.useSegmentedQueries : true,
    teamIds: server?.teamPermissions?.map(tp => tp.teamId) || [],
    userIds: server?.userPermissions?.map(up => up.userId) || []
  });
  const [argsText, setArgsText] = useState(
    server?.args ? JSON.stringify(server.args, null, 2) : '[]'
  );
  const [envText, setEnvText] = useState(
    server?.env ? JSON.stringify(server.env, null, 2) : '{}'
  );

  const handleTemplateChange = (templateKey) => {
    setSelectedTemplate(templateKey);
    if (templateKey !== 'custom') {
      const template = MCP_TEMPLATES[templateKey];
      setFormData({
        ...formData,
        name: template.name,
        type: template.type,
        description: template.description,
        command: template.command,
        args: template.args,
        env: template.env,
        icon: template.icon
      });
      setArgsText(JSON.stringify(template.args, null, 2));
      setEnvText(JSON.stringify(template.env, null, 2));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    try {
      const args = JSON.parse(argsText);
      const env = JSON.parse(envText);

      onSave({
        ...formData,
        args,
        env
      });
    } catch (err) {
      alert('Invalid JSON in args or env fields: ' + err.message);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b sticky top-0 bg-white">
          <h2 className="text-xl font-semibold">
            {server ? 'Edit MCP Server' : 'Add MCP Server'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {!server && (
            <div>
              <label className="block text-sm font-medium mb-2">Template</label>
              <select
                value={selectedTemplate}
                onChange={(e) => handleTemplateChange(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="custom">Custom Configuration</option>
                <option value="google-ads">Google Ads MCP</option>
                <option value="google-ads-keyword-planner">Google Ads Keyword Planner MCP</option>
                <option value="github">GitHub MCP</option>
                <option value="filesystem">Filesystem MCP</option>
                <option value="postgres">PostgreSQL MCP</option>
                <option value="slack">Slack MCP</option>
                <option value="dataforseo">DataForSEO MCP</option>
              </select>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium mb-2">Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Type *</label>
            <input
              type="text"
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., google-ads, github, custom"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              rows="2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Command *</label>
            <input
              type="text"
              value={formData.command}
              onChange={(e) => setFormData({ ...formData, command: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., pipx, npx, python"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Arguments (JSON array) *</label>
            <textarea
              value={argsText}
              onChange={(e) => setArgsText(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm"
              rows="4"
              placeholder='["arg1", "arg2"]'
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Environment Variables (JSON object)</label>
            <textarea
              value={envText}
              onChange={(e) => setEnvText(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm"
              rows="8"
              placeholder='{"KEY": "value"}'
            />
            <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-xs text-blue-800 font-medium mb-1">💡 For Google Ads MCP:</p>
              <p className="text-xs text-blue-700">
                Use <code className="bg-blue-100 px-1 rounded">GOOGLE_CLOUD_CREDENTIALS_JSON</code> and paste the entire JSON content from your credentials file (not the file path).
              </p>
              <p className="text-xs text-blue-600 mt-1">
                Example: <code className="bg-blue-100 px-1 rounded">{"{"}"type":"service_account","project_id":"...","private_key":"..."{"}"}</code>
              </p>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Leave values empty for users to configure their own credentials
            </p>
          </div>

          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.isGlobal}
                  onChange={(e) => setFormData({ ...formData, isGlobal: e.target.checked })}
                  className="rounded"
                />
                <span className="text-sm font-medium">Make Global (available to all users)</span>
              </label>

              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  className="rounded"
                />
                <span className="text-sm font-medium">Active</span>
              </label>
            </div>

            {/* Google Ads specific option */}
            {(formData.type === 'google-ads' || formData.type === 'google-ads-keyword-planner' || selectedTemplate === 'google-ads' || selectedTemplate === 'google-ads-keyword-planner') && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <label className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    checked={formData.useSegmentedQueries}
                    onChange={(e) => setFormData({ ...formData, useSegmentedQueries: e.target.checked })}
                    className="rounded mt-0.5"
                  />
                  <div className="flex-1">
                    <span className="text-sm font-medium text-blue-900">Use segmented queries (recommended)</span>
                    <p className="text-xs text-blue-700 mt-1">
                      Automatically adds <code className="bg-blue-100 px-1 rounded">segments.date</code> to metric queries and aggregates results client-side.
                      This prevents phantom/cached data issues in Google Ads API.
                    </p>
                    {!formData.useSegmentedQueries && (
                      <div className="mt-2 p-2 bg-yellow-50 border border-yellow-300 rounded flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                        <p className="text-xs text-yellow-800">
                          <strong>Warning:</strong> Disabling this may return inaccurate aggregated metrics due to API caching issues.
                        </p>
                      </div>
                    )}
                  </div>
                </label>
              </div>
            )}
          </div>

          {!formData.isGlobal && (
            <>
              <div>
                <label className="block text-sm font-medium mb-2">Teams with Access</label>
                <div className="border rounded-lg p-3 max-h-40 overflow-y-auto">
                  {teams.map(team => (
                    <label key={team.id} className="flex items-center gap-2 py-1">
                      <input
                        type="checkbox"
                        checked={formData.teamIds.includes(team.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData({ ...formData, teamIds: [...formData.teamIds, team.id] });
                          } else {
                            setFormData({ ...formData, teamIds: formData.teamIds.filter(id => id !== team.id) });
                          }
                        }}
                        className="rounded"
                      />
                      <span className="text-sm">{team.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            </>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <Check className="w-4 h-4" />
              {server ? 'Update' : 'Create'} Server
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Google Ads OAuth Authorization Component
const GoogleAdsOAuthSection = ({ server, onUpdate }) => {
  const [oauthStatus, setOAuthStatus] = useState({ authorized: false, loading: true });
  const [authorizing, setAuthorizing] = useState(false);

  useEffect(() => {
    checkOAuthStatus();
  }, [server.id]);

  const checkOAuthStatus = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/mcp-oauth/status/${server.id}`, {
        credentials: 'include'
      });
      const data = await response.json();
      setOAuthStatus({ ...data, loading: false });
    } catch (error) {
      console.error('Error checking OAuth status:', error);
      setOAuthStatus({ authorized: false, loading: false });
    }
  };

  const handleAuthorize = async () => {
    try {
      setAuthorizing(true);
      const response = await fetch(`${API_BASE_URL}/mcp-oauth/google-ads/authorize/${server.id}`, {
        credentials: 'include'
      });
      const data = await response.json();

      if (data.authUrl) {
        // Open OAuth flow in new window
        const width = 600;
        const height = 700;
        const left = window.screen.width / 2 - width / 2;
        const top = window.screen.height / 2 - height / 2;

        window.open(
          data.authUrl,
          'Google Ads Authorization',
          `width=${width},height=${height},left=${left},top=${top}`
        );

        // Listen for OAuth callback
        const handleOAuthCallback = (event) => {
          if (event.data && event.data.type === 'oauth-success') {
            checkOAuthStatus();
            onUpdate();
            window.removeEventListener('message', handleOAuthCallback);
          }
        };

        window.addEventListener('message', handleOAuthCallback);
      }
    } catch (error) {
      console.error('Error initiating OAuth:', error);
      alert('Failed to start authorization: ' + error.message);
    } finally {
      setAuthorizing(false);
    }
  };

  const handleRevoke = async () => {
    if (!window.confirm('Are you sure you want to revoke Google Ads access? Users will not be able to use this MCP until re-authorized.')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/mcp-oauth/revoke/${server.id}`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        checkOAuthStatus();
        onUpdate();
      }
    } catch (error) {
      console.error('Error revoking OAuth:', error);
      alert('Failed to revoke access: ' + error.message);
    }
  };

  if (oauthStatus.loading) {
    return (
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className={`p-4 border rounded-lg ${
      oauthStatus.authorized ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'
    }`}>
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          {oauthStatus.authorized ? (
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          ) : (
            <XCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
          )}
          <div className="flex-1">
            <h5 className="text-sm font-semibold mb-1">
              {oauthStatus.authorized ? 'Google Ads Authorized' : 'Authorization Required'}
            </h5>
            <p className="text-xs text-gray-700 mb-3">
              {oauthStatus.authorized
                ? 'This MCP is connected to your Google Ads account. All users can access it.'
                : 'Click "Authorize" to connect your Google Ads manager account. This is a one-time setup.'}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          {oauthStatus.authorized ? (
            <button
              onClick={handleRevoke}
              className="px-3 py-1.5 text-xs bg-white border border-red-300 text-red-700 rounded hover:bg-red-50 transition-colors flex items-center gap-1"
            >
              <XCircle className="w-3 h-3" />
              Revoke
            </button>
          ) : (
            <button
              onClick={handleAuthorize}
              disabled={authorizing}
              className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors flex items-center gap-1 disabled:opacity-50"
            >
              <Key className="w-3 h-3" />
              {authorizing ? 'Opening...' : 'Authorize Google Ads'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default MCPSettings;
